import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Card, CardContent, IconButton, Dialog, Button, Typography, Box, Stack, Drawer, useMediaQuery, Tooltip, SvgIcon } from '@mui/material';
import dayjs from 'dayjs';
import { KeyboardArrowDownOutlined as KeyboardArrowDownOutlinedIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AddWorkExperience from './AddWorkExperience';
import { useSnackbar } from 'components/snackbar';
import RemoveWorkExperience from './RemoveWorkExperienceDialog';
import { deleteWorkHistory } from 'api/users';
import { endpoints } from 'utils/axios';
import { changeNameFormat, toTitleCase, allCapitalize } from 'utils/Utils';
import { useAuth } from 'hooks/useAuthContext';
import AddWorkExperienceDrawer from 'components/basic-details-mobile-ui/AddWorkExperienceDrawer';
import { useTheme } from '@mui/material/styles';
import { EditSvgIcon, DeleteSvg } from 'components/icon/icon';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

/**
 * WorkExperienceDetails component displays and manages the work experience
 * details of a user, allowing them to add, edit, view, and delete work
 * experiences. It handles interactions for expanding sections, uploading
 * resumes, and checking for overlapping work experience data.
 *
 * @param {Function} setUpdating - Function to set the updating state.
 * @param {Array} workExperienceData - Array of work experience data.
 * @param {Function} onCancel - Function to handle cancel actions.
 * @param {Object} expandedSection - Object indicating which section is expanded.
 * @param {Function} handleExpand - Function to handle section expansion.
 * @param {Function} onWorkExperienceDataChange - Callback when work experience data changes.
 * @param {Array} certificateData - Array of certificate data.
 * @param {Array} educationData - Array of education data.
 * @param {Boolean} showWork - Boolean indicating if work details are shown.
 * @param {Function} handleShowWork - Function to handle showing of work details.
 * @param {Function} handleDeleteWorks - Function to handle deletion of work experiences.
 * @param {Boolean} uploadResume - Boolean indicating if resume uploading is enabled.
 */

const WorkExperienceDetails = ({ setUpdating, workExperienceData, onCancel, expandedSection, handleExpand, onWorkExperienceDataChange, certificateData, educationData, showWork, handleShowWork, handleDeleteWorks, uploadResume }) => {
  const theme = useTheme();
  const { user, updateContext } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showWorkHistory, setShowWorkHistory] = useState(false);
  const [workHistory, setWorkHistory] = useState([]);
  const [uploaded, setUploaded] = useState(false);
  const [localWork, setLocalWork] = useState([]);
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [currentCompany, setCurrentCompany] = useState("");
  const [isEmpty, setIsEmpty] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [idForScroll, setIdForScroll] = useState(null);
  const [scroll, setScroll] = useState(false);
  const [actionType, setActionType] = useState('');

  const workRef = useRef(null);
  const detailsRef = useRef(null);

  const [roleData, setRoleData] = useState([]);
  // const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [selected, setSelected] = useState({});
  const [isReadOnly, setReadOnly] = useState(showWork ? true : false);

  useEffect(() => {
    fetchDegreeData();
  }, []);

  useEffect(() => {
    setCurrentCompany('');
    const newWork = workExperienceData?.filter((data) =>
      !workHistory?.some((history) => history._id === data._id) &&
      data?.organization_name !== "Work Gap"
    );

    if (workExperienceData && workExperienceData?.length > 0) setIdForScroll(newWork[0]?._id || null);
    setWorkHistory(workExperienceData);
    setCurrentCompany(workExperienceData?.find(data => data?.present)?.organization_name)
  }, [workExperienceData, uploaded]);

  useEffect(() => {
    if (idForScroll && scroll) {
      scrollToChild(idForScroll);
      setIdForScroll(null);
    }
  }, [idForScroll]);

  useLayoutEffect(() => {
    if (expandedSection && expandedSection?.section === "work" && detailsRef?.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detailsRef, expandedSection]);

  /**
   * Scrolls smoothly to a child element with the specified targetId.
   * If the element exists, it will be scrolled into view smoothly,
   * aligning the start of the element with the start of the scrollable area.
   *
   * @param {string} targetId - The ID of the element to scroll to.
   */

  const scrollToChild = (targetId) => {
    if (targetId) {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  /**
   * Scrolls smoothly to the education section.
   * If the element exists, it will be scrolled into view smoothly,
   * aligning the start of the element with the start of the scrollable area.
   */
  const scrollToEducation = () => {
    workRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Handles the click event on a work experience card.
   * Expands or collapses the corresponding card section,
   * sets the selected card, and adjusts the read-only state
   * based on the card's status. For mobile devices, it opens
   * the details drawer; otherwise, it toggles the drawer.
   * 
   * @param {object} item - The work experience data.
   * @param {number} index - The index of the work card.
   */

  const handleCardClick = (item, index) => {
    handleExpand('work', index);
    setSelectedCard(item);
    setShowWorkHistory(false);
    item?.status !== 'active' && setReadOnly(false);
    if (isMobile) {
      setSelected(item);
      handleOpenDrawer();
    } else {
      handleCloseDrawer();
    }
  };

  /**
   * Handles the deletion of a work experience item.
   * If showWork is true, it removes the work experience item from local storage and updates the work experience data.
   * If showWork is false, it opens the confirmation dialog with the given id and title for deletion.
   * If the deletion fails, it shows an error message.
   * @param {string} workId - The id of the work experience item to be deleted.
   * @param {string} title - The title of the work experience item to be deleted.
   * @param {React.MouseEvent} event - The mouse event associated with the deletion request.
   */
  const handleDeleteWork = async (workId, title, event) => {
    event.stopPropagation();
    const work = workHistory.filter(work => work?.organization_name !== 'Work Gap');
    if (title !== 'Work Gap') {
      if (user?.stages >= 3 && work.length === 1) {
        enqueueSnackbar('The Work Experience field cannot be empty', { variant: 'error' });
        return;
      }
    }
    if (!workId) {
      localStorage.removeItem('workExperience');
      setWorkHistory([...workHistory?.filter(edu => edu?._id)]);
      setSelectedCard(null);
    } else {
      setActionType('workExperience')
      setItemId(workId);
      setTitle(title);
      handleOpenRemove();
    }
  };

  /**
   * Handles changes in the work experience data.
   * If the data has changed, it calls onWorkExperienceDataChange, sets scroll to true, and closes the details drawer.
   * If the data has not changed, it simply closes the details drawer.
   * @param {boolean} isChanged - Whether the work experience data has changed.
   */
  const handleDataChange = async (isChanged, workId) => {
    setShowWorkHistory(false);
    if (isChanged) {
      await onWorkExperienceDataChange();
      setScroll(true);
      if (workId) setIdForScroll(workId);
    }
    handleCloseDrawer();
  };

  /**
   * Resets the uploaded state and closes the work history section.
   * It is called after the user has uploaded a new work experience.
   */
  const handleUpload = () => {
    setShowWorkHistory(false);
    setUploaded(false);
  };


  /**
   * Closes the work experience dialog.
   * Sets the open state to false and the itemId state to an empty string.
   */
  const handleClose = () => {
    handleCloseRemove();
    setItemId('');
  };

  /**
   * Handles the deletion of a work experience item.
   * If showWork is true, it calls handleDeleteEdu with the workHistoryData's id and closes the dialog.
   * If showWork is false, it calls deleteWorkHistory with the workHistoryData's id and updates the work experience data if the deletion is successful.
   * If the deletion fails, it shows an error message.
   * In any case, it closes the dialog.
   */
  const handleDeleteAction = async () => {
    if (showWork) {
      handleDeleteEdu(itemId);
      return;
    }
    try {
      if (actionType == 'workExperience') {
        setUpdating(true);
        const response = await deleteWorkHistory({ workId: itemId });
        if (response && response?.success) {
          // handleCloseRemove();
          setWorkHistory(response?.data || []);
          onWorkExperienceDataChange();
          handleCloseDrawer();
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
      }
      handleClose();
    } catch (e) {
      setUpdating(false);
    }
  };

  /**
   * Handles the addition of a new work experience item.
   * Sets showWorkHistory to true, expands the work experience section with the given index, sets selectedCard to an empty object, and sets readOnly to false.
   */
  const handleAddNewEdu = () => {
    setShowWorkHistory(true);
    handleExpand("work", workHistory.length);
    setSelectedCard({});
    setReadOnly(false);
  };
  /**
   * Fetches the role data from the server and sets the role data state to the fetched data.
   * The role data is deduplicated using a Set.
   * @memberof WorkExperienceSection
   * @returns {Promise<void>} - Resolves when the roles are fetched successfully, rejects if there's an error.
   */
  const fetchDegreeData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.roles}`);
    const resultData = await response.json();
    setRoleData([...new Set([...resultData])]);
  };
  /**
   * Checks if the given data overlaps with any existing education, certificate, or work experience entry.
   * It returns an object with a boolean indicating overlap and the type of overlap (education, certificate, or work experience) and the degree/certificate title.
   * @param {object} data - The data to be checked for overlap.
   * @returns {object} - The overlap object with isOverlapping, type, and degree/certificate title.
   */
  const checkingOverlapping = (data) => {
    /**
     * Checks if the given data overlaps with any existing education, certificate, or work experience entry.
     * @param {array} items - The array of items to check for overlap.
     * @param {string} fromKey - The key for the start date of the item.
     * @param {string} toKey - The key for the end date of the item.
     * @param {string} typeLabel - The type of the item (education, certificate, or work experience).
     * @param {string} degreeKey - The key for the degree title of the item.
     * @returns {object} - The overlap object with isOverlapping, type, and degree/certificate title.
     */
    const checkOverlap = (items, fromKey, toKey, typeLabel, degreeKey) => {
      for (let i = 0; i < items.length; i++) {
        const fromDate = new Date(items[i][fromKey]);
        const toDate = typeLabel === 'work experience' ? (items[i]?.present ? new Date() : new Date(items[i][toKey])) : new Date(items[i][toKey]);
        const dataFromDate = new Date(data?.work_history_from);
        const dataToDate = data?.present ? new Date() : new Date(data?.work_history_to);

        if (fromDate <= dataToDate && dataFromDate <= toDate) {
          return { isOverlapping: true, type: typeLabel, degree: items[i][degreeKey] };
        }
      }
      return { isOverlapping: false };
    };

    const filterWork = workExperienceData?.filter(
      edu => !(edu?._id === data?._id || edu?.organization_name === 'Work Gap')
    );

    const filterEducation = educationData?.filter(edu => edu?._id !== data?._id && edu?.learning_format === "Full time - offline");
    const educationOverlap = checkOverlap(filterEducation, 'education_from', 'education_to', 'education', 'degree_received');
    if (educationOverlap.isOverlapping) return educationOverlap;

    const filterCertificate = certificateData?.filter(edu => edu?._id !== data?._id && edu?.learning_format === "Full time - offline");
    const certificateOverlap = checkOverlap(filterCertificate, 'education_from', 'education_to', 'certificate', 'certificate_received');
    if (certificateOverlap.isOverlapping) return certificateOverlap;

    const workOverlap = checkOverlap(filterWork, 'work_history_from', 'work_history_to', 'work experience', 'last_job_function');
    if (workOverlap.isOverlapping) return workOverlap;

    return { isOverlapping: false };
  };

  /**
   * Handles when a user selects a work experience from the education section.
   * It closes the education section, removes the work experience from local storage,
   * and opens the work experience drawer with the selected work experience.
   * @param {object} workExperience - The selected work experience object.
   */
  const handleEducationResume = (workExperience, id) => {
    localStorage.removeItem('workExperience');
    setShowWorkHistory(false);
    handleCloseDrawer();
    setScroll(true);
    if (id) setIdForScroll(id);
    handleShowWork('WorkHistory', workExperience);
  }
  /**
   * Handles when the user selects a work experience to view in the resume form.
   * Stores the selected work experience in the selectedCard state variable, opens the details drawer, and enables read only mode if the status of the work experience is not 'active'.
   * @param {Object} work - The work experience object to be passed to the details drawer.
   */
  const handleOpenWorkDrawer = (work) => {
    setSelected(work);
    handleOpenDrawer();
    work?.status !== 'active' && setReadOnly(false);
  }

  /**
   * Handles the deletion of an education.
   * Calls handleDeleteWorks with the workId and closes the dialog.
   * If the deletion is successful, it updates the education data and closes the details drawer.
   * @param {string} workId - The id of the work experience to be deleted.
   */
  const handleDeleteEdu = (workId) => {
    handleDeleteWorks(workId);
    handleClose();
    handleCloseDrawer();
  };

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openDetailsDrawer = dialogIds.includes("workExperience");
  const openRemove = dialogIds.includes("removeWorkExperience");

  useEffect(() => {
    if (!openDetailsDrawer) {
      setShowWorkHistory(false);
      setSelectedCard(null);
      onCancel();
      handleDataChange();
      showWork && setReadOnly(true);
    }
  }, [openDetailsDrawer]);

  const handleCloseDrawer = () => {
    const timer = setTimeout(() => {
      closeDialog('workExperience');
    }, 300);
    // return () => clearTimeout(timer);
  };
  const handleOpenDrawer = () => {
    openDialog('workExperience');
  };
  const handleCloseRemove = () => {
    closeDialog('removeWorkExperience');
  };
  const handleOpenRemove = () => {
    openDialog('removeWorkExperience');
  };

  return (
    <Box className="candidate-basic-det-wrapper" sx={{ position: 'relative' }}>
      <Box mb={3}>
        <Typography className="step-heading xs-fs-16" variant="h6" gutterBottom>
          Your Work Experience
        </Typography>
        <Typography className="step-info xs-fs-12" variant="body2" gutterBottom>
          {uploadResume ?
            'For best results, please include a comprehensive list of all your work experiences.'
            : 'This would help us identify your core competencies & skillset'}
        </Typography>
      </Box>
      {workHistory.filter(edu => !((!edu?.gap_title || !edu?.gap_explaination) && edu?.organization_name === 'Work Gap'))?.map((data, index) => (
        <Box mb={3} key={index}>
          {expandedSection && expandedSection?.section === 'work' && expandedSection?.index === index ? (
            <Box ref={detailsRef} className={isReadOnly && "update-resume-accord-wrapper"}>
              <Card className={isReadOnly ? "info-list-item" : "form-wrapper"} variant="outlined">
                <CardContent sx={{ padding: isReadOnly ? '0!important' : '24px !important' }}>
                  <AddWorkExperience
                    key={data?._id}
                    onCancel={() => {
                      setShowWorkHistory(false);
                      setSelectedCard(null);
                      onCancel();
                      showWork && setReadOnly(true);
                    }}
                    handleArrowClick={() => {
                      onCancel();
                      showWork && setReadOnly(true);
                    }}
                    onUploadSuccess={handleUpload}
                    id={data?._id}
                    workHistoryData={data}
                    onWorkExperienceDataChange={handleDataChange}
                    currentCompany={currentCompany}
                    roleData={roleData}
                    checkingOverlapping={checkingOverlapping}
                    showWork={showWork}
                    handleShowWork={handleEducationResume}
                    handleDeleteEdu={handleDeleteEdu}
                    workHistoryLength={workHistory.filter(work => work?.organization_name !== 'Work Gap').length}
                    isReadOnly={isReadOnly}
                    setReadOnly={setReadOnly}
                  />
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box className={showWork && "update-resume-accord-wrapper"}>
              <Card id={idForScroll === data?._id ? idForScroll : null} className={`info-list-item ${data?.status !== "active" ? "incomplete-details" : ""}`} variant="outlined" key={data._id} sx={{ marginTop: 1 }}
                onClick={() => handleCardClick(data, index)} >
                <CardContent sx={{ padding: showWork && '0!important' }}>
                  <Box className="job-update-form-top" sx={{ marginBottom: showWork ? '20px !important' : '0px !important' }}>
                    <Stack direction="row" justifyContent={'space-between'} spacing={2}>
                      <Box>
                        <Stack direction="row" alignItems={'center'} spacing={1.5}>
                          <Typography variant="h6" className="card-header-title">
                            {data?.organization_name === 'Work Gap' ? `CAREER BREAK: ${allCapitalize(data?.gap_title || data?.highest_designation)}` :
                              (data?.highest_designation ? (`${data?.highest_designation} `) + 'at ' : '') +
                              (data?.organization_name ? allCapitalize(`${data?.organization_name}`) : 'Work Experience Details')
                            }
                          </Typography>
                          {data?.status !== "active" && <Box className="incomplete-tag">Incomplete</Box>}
                        </Stack>
                        <Typography className="info-wrapper" variant="body2" gutterBottom>
                          {data?.work_history_from ? `${dayjs(data?.work_history_from).format('MMM YYYY')} - ${data?.present ? 'Present' : data?.work_history_to && dayjs(data?.work_history_to).format('MMM YYYY')}` : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems={'center'} spacing={2}>
                        {data?.status !== "active" &&
                          <IconButton
                            className="delete-sec-btn"
                            aria-label="delete"
                            onClick={(event) => handleDeleteWork(data._id, data.organization_name, event)}
                          >
                            <DeleteSvg />
                          </IconButton>}
                        <KeyboardArrowDownOutlinedIcon />
                      </Stack>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Box>)}
        </Box>
      ))}
      {(showWorkHistory && expandedSection && expandedSection?.section === 'work' && (
        <Box ref={detailsRef} className={isReadOnly && "update-resume-accord-wrapper"}>
          <Card className={isReadOnly ? "info-list-item" : "form-wrapper"} variant="outlined">
            <CardContent sx={{ padding: isReadOnly ? '0!important' : '24px !important' }}>
              <AddWorkExperience
                onCancel={() => {
                  setShowWorkHistory(false);
                  setSelectedCard(null);
                  onCancel();
                  showWork && setReadOnly(true);
                }}
                handleArrowClick={() => {
                  onCancel();
                  showWork && setReadOnly(true);
                }}
                onUploadSuccess={handleUpload}
                onWorkExperienceDataChange={handleDataChange}
                currentCompany={currentCompany}
                roleData={roleData}
                checkingOverlapping={checkingOverlapping}
                showWork={showWork}
                handleShowWork={handleEducationResume}
                handleDeleteEdu={handleDeleteEdu}
                workHistoryLength={workHistory.filter(work => work?.organization_name !== 'Work Gap').length}
                isReadOnly={isReadOnly}
                setReadOnly={setReadOnly}
              />
            </CardContent>
          </Card>
        </Box>
      ))}
      <Box mt={2}>
        {(!selectedCard || selectedCard?.status || expandedSection?.section !== 'work') &&
          <Button
            className="add-btn"
            disableRipple
            startIcon={<AddIcon />}
            disabled={workHistory?.length > 0 && workHistory?.some(edu => !edu?._id)}
            onClick={() => isMobile ? handleOpenWorkDrawer() : handleAddNewEdu()}>
            Add Work Experience
          </Button>}
      </Box>
      {/* remove work experience */}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={openRemove}
          onClose={handleClose}
          className="forgot-password-dialog otp-dialog-wrapper"
        >
          <RemoveWorkExperience onClose={handleClose} onRemove={handleDeleteAction} title={title} />
        </Drawer> :
        <Dialog
          open={openRemove}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="otp-dialog-wrapper"
        >
          <RemoveWorkExperience onClose={handleClose} onRemove={handleDeleteAction} title={title} />
        </Dialog>}

      <Drawer className="mob-side-panel" open={openDetailsDrawer} anchor={'right'} >
        <AddWorkExperienceDrawer
          id={selected?._id}
          workHistoryData={selected}
          onCancel={() => {
            setShowWorkHistory(false);
            setSelectedCard(null);
            onCancel();
            // handleCloseDrawer();
            showWork && setReadOnly(true);
          }}
          handleArrowClick={() => {
            handleDataChange(true);
            // handleCloseDrawer();
            onCancel();
            showWork && setReadOnly(true);
          }}
          onUploadSuccess={handleUpload}
          onWorkExperienceDataChange={handleDataChange}
          currentCompany={currentCompany}
          roleData={roleData}
          checkingOverlapping={checkingOverlapping}
          handleDeleteWork={handleDeleteWork}
          selected={selected}
          workHistoryLength={workHistory?.filter(work => work?.organization_name !== 'Work Gap').length}
          showWork={showWork}
          handleShowWork={handleEducationResume}
          handleDeleteEdu={handleDeleteEdu}
          isReadOnly={isReadOnly}
          setReadOnly={setReadOnly}
          deleteIncompleteSection={() => {
            setShowWorkHistory(false);
            setSelectedCard(null);
            localStorage.removeItem('workExperience');
            onCancel();
            handleCloseDrawer();
            showWork && setReadOnly(true);
          }}
        />
      </Drawer>
    </Box>
  );
};

export default WorkExperienceDetails